import { type HaNNodeSelection, HaNViewStoreContext } from "@lib/logic";
import { useRequiredContext } from "@lib/utils";

import { useConfirmDiscardingBooking } from "../details/useConfirmDiscardingBooking";
import { useFitNodeInView } from "../map/useFitNodeInView";
import { HaNContext } from "../shell/HaNContext";

/** A result in a tab's list or carousel: its node and the floor it's on. */
export type HaNResult = { id: string; floorId: string };

/**
 * Opens a result's details from the list or the carousel. A result on another floor takes the page to that floor.
 * Asks first when that would throw away a booking with changes. `onOpened` runs only when it opens, not when the user
 * keeps the booking they were making.
 */
export const useOpenResult = () => {
  const { tab, floor } = useRequiredContext(HaNContext);
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const confirmDiscardingBooking = useConfirmDiscardingBooking();
  const fitNodeInView = useFitNodeInView();

  return (kind: HaNNodeSelection["kind"], result: HaNResult, { onOpened }: { onOpened?: () => void } = {}) => {
    confirmDiscardingBooking(() => {
      onOpened?.();

      const isOnShownFloor = result.floorId === floor.floorId;

      if (!isOnShownFloor) floor.selectFloor(result.floorId);

      viewStore.select(tab, { kind, id: result.id }, { openDetails: true });

      // Another floor's map is still being built, and shows the whole floor when it's ready
      if (isOnShownFloor) fitNodeInView(result.id);
    }, result.id);
  };
};
